import { useEffect, useState } from "react";
import { api, errMsg, API } from "@/lib/api";
import { toast } from "sonner";
import { Package, FileCode, Download, ShieldCheck, Github, Palette, AlertTriangle,
  Eye, Play, GitCompare, Boxes, Save, Pencil, Sparkles, RefreshCw, Image as ImageIcon,
  ThumbsUp, ThumbsDown } from "lucide-react";
import { DepthLabel } from "@/components/Shell";
import ProcessLog from "@/components/ProcessLog";
import { Glossary } from "@/components/kit";
import { useCurrency } from "@/context/CurrencyContext";

// SW9 — optional one-tap satisfaction after evidence-pack delivery. No reward attached (so it can't
// be gamed), recorded once per build, feeding a public aggregate "founder-reported time saved" stat.
function EvidenceSatisfaction({ projectId, buildId }) {
  const KEY = `kadana_sat_${buildId}`;
  const [done, setDone] = useState(() => { try { return !!localStorage.getItem(KEY); } catch { return false; } });
  const [agg, setAgg] = useState(null);
  const loadAgg = () => api.get("/public/founder-time-saved").then(r => setAgg(r.data)).catch(() => {});
  useEffect(() => { loadAgg(); }, []);
  const tap = async (rating) => {
    try { await api.post(`/projects/${projectId}/satisfaction`, { rating }); } catch { /* optional */ }
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    setDone(true); loadAgg();
  };
  return (
    <div data-testid="evidence-satisfaction" style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
      {!done ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span className="k-muted" style={{ fontSize: 13 }}>Did Kadana save you time on this? (optional — no reward)</span>
          <button className="k-btn k-btn-ghost" data-testid="evidence-sat-yes" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => tap("up")}><ThumbsUp size={13} /> Yes</button>
          <button className="k-btn k-btn-ghost" data-testid="evidence-sat-no" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => tap("down")}><ThumbsDown size={13} /> Not really</button>
        </div>
      ) : (
        <p className="k-muted" data-testid="evidence-sat-done" style={{ fontSize: 12 }}>Thanks — recorded. No reward is attached, so the stat can't be gamed.</p>
      )}
      {agg && agg.responses > 0 && (
        <p className="k-muted" data-testid="founder-time-saved-stat" style={{ fontSize: 11, marginTop: 6 }}>
          {agg.positive_pct}% of {agg.responses} founders said Kadana saved them time.
        </p>
      )}
    </div>
  );
}

// #40 template versioning + changelog (surfaced per template)
const TEMPLATE_VERSIONS = {
  _default: { v: "1.4.0", log: "Added accessibility statement + cookie Accept/Reject parity; DSAR export scaffold." },
};
const FRAMEWORKS = [["react_fastapi", "React + FastAPI (default)"], ["react_node", "React + Node/Express"], ["next_fastapi", "Next.js + FastAPI"]];
const SEED_MODES = [["none", "No seed data"], ["minimal", "Minimal"], ["realistic", "Realistic"]];

export default function GenerateTab({ projectId }) {
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("");
  const [blueprints, setBlueprints] = useState([]);
  const [chosenBps, setChosenBps] = useState([]);
  const [planData, setPlanData] = useState(null);
  const [quote, setQuote] = useState(null);
  const [build, setBuild] = useState(null);
  const [openFile, setOpenFile] = useState(null);
  const [brand, setBrand] = useState({ primary: "#7a7d80", text: "#8a8d90" });
  const [brandRes, setBrandRes] = useState(null);
  const [logo, setLogo] = useState(null);              // #47 logo upload
  const [aiReview, setAiReview] = useState(false);
  const [balance, setBalance] = useState(null);
  const [device, setDevice] = useState("web");
  // #45/#46/#48 build options
  const [opts, setOpts] = useState({ framework: "react_fastapi", seed: "minimal", seedVolume: 20, pwa: false });
  const [building, setBuilding] = useState(false);      // #50 build status
  const [previewHtml, setPreviewHtml] = useState(null); // #38
  const [editing, setEditing] = useState(null);         // #41 {path, content}
  const [explain, setExplain] = useState(null);         // #44
  const [baseline, setBaseline] = useState(null);       // #43 diff baseline
  const [diff, setDiff] = useState(null);
  const [creditPriceMinor, setCreditPriceMinor] = useState(null); // Patch 51 — GBP per-credit (minor)
  const { format: fmtCur, isGbp, active: curActive } = useCurrency();

  useEffect(() => {
    api.get("/generation/templates").then(r => setTemplates(r.data.templates)).catch(() => {});
    api.get("/generation/blueprints").then(r => setBlueprints(r.data.blueprints.filter(b => b.scaffold_available))).catch(() => {});
    api.get(`/projects/${projectId}`).then(r => {
      const orgId = r.data.org_id;
      if (orgId) api.get(`/orgs/${orgId}/balance`).then(b => setBalance(b.data.balance_credits)).catch(() => {});
    }).catch(() => {});
    try { const b = localStorage.getItem(`kd_build_baseline_${projectId}`); if (b) setBaseline(JSON.parse(b)); } catch { /* ignore */ }
    api.get("/pricing").then(r => setCreditPriceMinor(r.data.credit_list_price_minor)).catch(() => {});
  }, [projectId]);

  // Patch 51 — the fixed quote's approximate value in the visitor's chosen display currency (GBP billed).
  const quoteGbp = quote && creditPriceMinor != null ? (quote.quote_credits * creditPriceMinor) / 100 : null;

  const DEVICES = [["web", "Web", true], ["ios", "iOS", false], ["android", "Android", false], ["ussd", "USSD", false]];
  const lowBalance = quote && balance != null && balance < quote.quote_credits;
  const ver = TEMPLATE_VERSIONS._default;

  const doPlan = async (id) => {
    setTpl(id); setQuote(null); setBuild(null);
    try { const { data } = await api.post("/generation/plan", { project_id: projectId, template_id: id, blueprint_ids: chosenBps }); setPlanData(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const getQuote = async () => {
    try { const { data } = await api.post("/quotes", { project_id: projectId, complexity: 6, feature_count: 4 }); setQuote(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const confirmQuote = async () => {
    try { await api.post(`/quotes/${quote.id}/confirm`); setQuote({ ...quote, status: "confirmed" }); toast.success("Quote confirmed"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doBuild = async () => {
    setBuilding(true);
    try {
      const { data } = await api.post("/generation/build", {
        project_id: projectId, template_id: tpl, quote_id: quote.id, blueprint_ids: chosenBps,
        brand_colours: brandRes ? brand : {}, ai_copy_review: aiReview });
      const full = await api.get(`/generation/builds/${data.build_id}`);
      setBuild(full.data); setDiff(null); toast.success("Generated app + evidence pack ready");
    } catch (e) { toast.error(errMsg(e)); } finally { setBuilding(false); }
  };
  const runBrand = async () => {
    try { const { data } = await api.post("/generation/brand-kit", { project_id: projectId, colours: brand, tone: "professional" }); setBrandRes(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const viewFile = async (path) => {
    setExplain(null); setEditing(null);
    try { const { data } = await api.get(`/generation/builds/${build.id}/file`, { params: { path } }); setOpenFile({ ...data, path }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const regenModule = async () => {   // #39 re-emit a single module (sandbox: re-fetch)
    if (!openFile) return;
    await viewFile(openFile.path);
    toast.success(`Re-emitted ${openFile.path} (sandbox)`);
  };
  const explainFile = async () => {   // #44 explain via Copilot
    setExplain({ loading: true, text: "" });
    try {
      const { data } = await api.post("/copilot", { mode: "moderate",
        query: `Explain what this generated file does, and any risks to review before production:\n\nFile: ${openFile.path}\n\n${(openFile.content || "").slice(0, 4000)}` });
      setExplain({ loading: false, text: data.reply });
    } catch (e) { setExplain(null); toast.error(errMsg(e)); }
  };
  const doPreview = async () => {     // #38 live preview
    const htmlPath = build.file_list.find(p => /\.html$/i.test(p)) || build.file_list.find(p => /index/i.test(p));
    if (!htmlPath) { toast.error("No previewable HTML file in this build."); return; }
    try { const { data } = await api.get(`/generation/builds/${build.id}/file`, { params: { path: htmlPath } }); setPreviewHtml(data.content || ""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const downloadText = (name, text) => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };
  const dockerBundle = () => {        // #49 docker-compose + .env.example
    downloadText("docker-compose.yml",
`version: "3.9"
services:
  api:
    build: ./backend
    env_file: .env
    ports: ["8001:8001"]
    depends_on: [db]
  web:
    build: ./frontend
    ports: ["3000:3000"]
    depends_on: [api]
  db:
    image: postgres:16
    environment:
      POSTGRES_PASSWORD: \${POSTGRES_PASSWORD}
    volumes: ["pgdata:/var/lib/postgresql/data"]
volumes:
  pgdata:
`);
    downloadText(".env.example",
`# Kadana-generated environment — fill in your own credentials (never commit real values)
DATABASE_URL=postgresql://postgres:changeme@db:5432/app
POSTGRES_PASSWORD=changeme
JWT_SECRET=change-me
# Build options
KADANA_FRAMEWORK=${opts.framework}
KADANA_SEED=${opts.seed}
KADANA_SEED_VOLUME=${opts.seedVolume}
KADANA_PWA=${opts.pwa}
`);
    toast.success("Downloaded docker-compose.yml + .env.example");
  };
  const pinBaseline = () => { setBaseline(build.file_list); localStorage.setItem(`kd_build_baseline_${projectId}`, JSON.stringify(build.file_list)); toast.success("Pinned this build's file list as the diff baseline"); };
  const runDiff = () => {
    if (!baseline) { toast.error("Pin a baseline first, then rebuild to compare."); return; }
    const cur = new Set(build.file_list), base = new Set(baseline);
    setDiff({ added: build.file_list.filter(p => !base.has(p)), removed: baseline.filter(p => !cur.has(p)) });
  };
  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = () => setLogo(r.result); r.readAsDataURL(f);
  };

  const token = document.cookie.match(/access_token=([^;]+)/)?.[1];
  const dl = (suffix) => `${API}/generation/builds/${build.id}/${suffix}${token ? `?auth=${token}` : ""}`;

  return (
    <div data-testid="generate-tab">
      <div className="k-container" style={{ padding: 14, marginBottom: 14 }} data-testid="device-switcher">
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {DEVICES.map(([k, label, live]) => (
            <button key={k} data-testid={`device-${k}`} disabled={!live}
              className={`k-badge ${device === k && live ? "k-badge-accent" : ""}`}
              style={{ cursor: live ? "pointer" : "not-allowed", opacity: live ? 1 : 0.55 }}
              title={live ? "" : "Coming in a later phase (Phase 3B)"}
              onClick={() => live && setDevice(k)}>{label}{!live ? " · coming" : ""}</button>
          ))}
          {/* #50 build status/queue */}
          <span data-testid="build-status" className={`k-badge ${building ? "k-badge-sandbox" : build ? "k-badge-accent" : ""}`} style={{ marginLeft: "auto" }}>
            {building ? "Building…" : build ? `Build ${build.status || "ready"}` : "Idle"}
          </span>
        </div>
      </div>

      <div className="k-container" style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <Package size={18} color="var(--accent-primary)" />
          <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Choose a template</h3>
          <span className="k-badge mono" data-testid="template-version" title={ver.log} style={{ marginLeft: "auto" }}>v{ver.v}</span>
        </div>
        <p className="k-muted" style={{ fontSize: 12, marginTop: 0 }} data-testid="template-changelog">Latest changes: {ver.log}</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 12 }}>
          {templates.map(t => (
            <div key={t.id} className="k-control" data-testid={`template-${t.id}`} style={{ cursor: "pointer", borderColor: tpl === t.id ? "var(--accent-primary)" : undefined }}
              onClick={() => doPlan(t.id)}>
              <div className="k-heading" style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</div>
              <div className="k-muted" style={{ fontSize: 12, marginTop: 4 }}>{t.summary}</div>
            </div>
          ))}
        </div>
      </div>

      {/* #45/#46/#48 build options */}
      <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="build-options">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Boxes size={16} color="var(--accent-primary)" /><h3 className="k-heading" style={{ fontSize: 15, margin: 0 }}>Build options</h3>
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
          <label className="k-muted" style={{ fontSize: 12 }}>Framework&nbsp;
            <select className="k-control" data-testid="opt-framework" style={{ width: "auto", display: "inline-block" }}
              value={opts.framework} onChange={e => setOpts({ ...opts, framework: e.target.value })}>
              {FRAMEWORKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="k-muted" style={{ fontSize: 12 }}>Seed data&nbsp;
            <select className="k-control" data-testid="opt-seed" style={{ width: "auto", display: "inline-block" }}
              value={opts.seed} onChange={e => setOpts({ ...opts, seed: e.target.value })}>
              {SEED_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          {opts.seed !== "none" && (
            <label className="k-muted" style={{ fontSize: 12 }}>Rows&nbsp;
              <input type="number" min={1} max={500} className="k-control" data-testid="opt-seed-volume"
                style={{ width: 80, display: "inline-block" }} value={opts.seedVolume}
                onChange={e => setOpts({ ...opts, seedVolume: Number(e.target.value) })} />
            </label>
          )}
          <label className="k-muted" style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" data-testid="opt-pwa" checked={opts.pwa} onChange={e => setOpts({ ...opts, pwa: e.target.checked })} /> PWA / offline
          </label>
        </div>
        <p className="k-muted" style={{ fontSize: 11, marginTop: 8 }}>Options are written into the downloadable Docker bundle and .env.example so your export is reproducible.</p>
      </div>

      {blueprints.length > 0 && (
        <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="blueprint-picker">
          <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0 }}>Phase 1C blueprint scaffolds (confirm each)</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {blueprints.map(b => (
              <button key={b.id} data-testid={`bp-${b.id}`} className={`k-badge ${chosenBps.includes(b.id) ? "k-badge-accent" : ""}`}
                style={{ cursor: "pointer" }} title={b.note}
                onClick={() => setChosenBps(c => c.includes(b.id) ? c.filter(x => x !== b.id) : [...c, b.id])}>
                {b.id} · {b.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {planData && (
        <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="plan-result">
          <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0 }}>Plan · {planData.file_list.length} files</h3>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {planData.region_profiles.map(p => <span key={p.id} className="k-badge">{p.name} <DepthLabel depth={p.depth} /></span>)}
          </div>
          {planData.connectors.map(c => (
            <div key={c.id} className="k-muted" style={{ fontSize: 12 }} data-testid={`plan-connector-${c.id}`}>
              {c.name} · class {c.access_class} · {c.status}
              {c.must_obtain && <span className="k-tag-warn"> · you must obtain your own access</span>}
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
            {!quote && <button className="k-btn k-btn-primary" data-testid="gen-get-quote" onClick={getQuote}>Get fixed quote</button>}
            {quote && quote.status !== "confirmed" && <><span className="k-badge k-badge-accent">{Number(quote.quote_credits).toLocaleString("en-GB")} credits</span>
              <button className="k-btn k-btn-primary" data-testid="gen-confirm-quote" onClick={confirmQuote}>Confirm quote</button></>}
            {quote?.status === "confirmed" && !build && !lowBalance && <button className="k-btn k-btn-primary" data-testid="gen-build" onClick={doBuild} disabled={building}>{building ? "Generating…" : "Generate app"}</button>}
            {quote?.status === "confirmed" && !build && lowBalance && (
              <span data-testid="credit-lock" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button className="k-btn k-btn-primary" disabled title="Balance below quote">Generate app</button>
                <span className="k-tag-warn" style={{ fontSize: 12 }}>Balance {balance} &lt; quote {quote.quote_credits}.</span>
                <a className="k-link" data-testid="credit-lock-topup" href="/billing">Top up balance</a>
              </span>
            )}
            <label className="k-muted" style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" data-testid="ai-review-toggle" checked={aiReview} onChange={e => setAiReview(e.target.checked)} /> AI copy review
            </label>
          </div>
          {quote && quoteGbp != null && (
            <div data-testid="quote-fixed-price" className="k-muted" style={{ fontSize: 13, marginTop: 10 }}>
              Fixed build price: <span className="k-heading" style={{ fontWeight: 700 }}>GBP {quoteGbp.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              {!isGbp && (
                <span data-testid="quote-fixed-price-converted"> · ≈ {fmtCur(quoteGbp)} {curActive.code}
                  <span className="k-muted" style={{ fontSize: 11 }}> (approximate; billed in GBP)</span>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* #47 brand kit + logo */}
      <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="brand-kit">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Palette size={16} color="var(--accent-primary)" /><h3 className="k-heading" style={{ fontSize: 15, margin: 0 }}>Brand kit</h3>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label className="k-muted" style={{ fontSize: 12 }}>Primary <input type="color" data-testid="brand-primary" value={brand.primary} onChange={e => setBrand({ ...brand, primary: e.target.value })} /></label>
          <label className="k-muted" style={{ fontSize: 12 }}>Text <input type="color" data-testid="brand-text" value={brand.text} onChange={e => setBrand({ ...brand, text: e.target.value })} /></label>
          <label className="k-btn k-btn-ghost" data-testid="brand-logo-label" style={{ padding: "6px 12px", fontSize: 13, cursor: "pointer" }}>
            <ImageIcon size={14} /> Upload logo
            <input type="file" accept="image/*" data-testid="brand-logo" onChange={onLogo} style={{ display: "none" }} />
          </label>
          {logo && <img src={logo} alt="logo preview" data-testid="brand-logo-preview" style={{ height: 32, borderRadius: 6, border: "1px solid var(--border)" }} />}
          <button className="k-btn k-btn-ghost" data-testid="brand-run" style={{ padding: "6px 12px", fontSize: 13 }} onClick={runBrand}>Check contrast</button>
        </div>
        {brandRes && brandRes.contrast_findings.map((f, i) => (
          <div key={i} className="k-muted" style={{ fontSize: 12, marginTop: 6 }} data-testid={`contrast-${f.pair}`}>
            {f.pair}: ratio {f.ratio} {f.passes_AA ? <span className="k-tag-green">AA pass</span> :
              <span className="k-tag-warn">below AA — propose {f.proposed}</span>}
          </div>
        ))}
      </div>

      {build && (
        <>
          {/* #37 build log / stage commentary */}
          <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="build-log">
            <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0 }}>Build log</h3>
            <ProcessLog projectId={projectId} />
          </div>

          <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="summary-card">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <ShieldCheck size={16} color="var(--accent-primary)" /><h3 className="k-heading" style={{ fontSize: 15, margin: 0 }}>Post-build summary</h3>
              <span className="k-badge k-badge-sandbox" style={{ marginLeft: "auto" }}>UNSIGNED</span>
            </div>
            <div className="k-muted" style={{ fontSize: 12, marginBottom: 6 }}><Glossary term="Evidence pack">What is the evidence pack?</Glossary></div>
            <div className="k-muted" style={{ fontSize: 13 }}>
              {build.summary_card.files_created} files · {build.summary_card.database_tables} schema files · {build.summary_card.ui_components} components · {build.summary_card.endpoints.length} endpoints
            </div>
            <div className="k-muted" style={{ fontSize: 12, marginTop: 6 }}>Checks that ran: {[...new Set(build.summary_card.checks_that_ran.map(c => c.tool))].join(", ")}</div>
            {build.open_items.length > 0 && (
              <div style={{ marginTop: 8 }} data-testid="open-items">
                <div className="k-tag-warn" style={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}><AlertTriangle size={14} /> {build.open_items.length} open items</div>
                {build.open_items.slice(0, 8).map((o, i) => (
                  <div key={i} className="k-muted" style={{ fontSize: 12 }}>
                    <span className={o.severity === "critical" ? "k-tag-red" : "k-tag-warn"}>[{o.severity}]</span> {o.message} <span style={{ opacity: .7 }}>({o.file})</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <a className="k-btn k-btn-primary" data-testid="download-zip" href={dl("download")}><Download size={14} /> Download ZIP</a>
              <a className="k-btn k-btn-ghost" data-testid="download-evidence-pdf" href={dl("evidence.pdf")} target="_blank" rel="noreferrer"><FileCode size={14} /> Evidence pack (PDF)</a>
              <button className="k-btn k-btn-ghost" data-testid="download-docker" onClick={dockerBundle}><Boxes size={14} /> Docker bundle</button>
              <button className="k-btn k-btn-ghost" data-testid="preview-btn" onClick={doPreview}><Eye size={14} /> Live preview</button>
              <button className="k-btn k-btn-ghost" data-testid="pin-baseline" onClick={pinBaseline}><GitCompare size={14} /> Pin as diff baseline</button>
              <button className="k-btn k-btn-ghost" data-testid="run-diff" onClick={runDiff}><GitCompare size={14} /> Diff vs baseline</button>
              <button className="k-btn k-btn-ghost" data-testid="github-push" onClick={async () => { const { data } = await api.post(`/generation/builds/${build.id}/github-push`); toast(data.message); }}><Github size={14} /> Push to GitHub</button>
            </div>
            {diff && (
              <div className="k-control" data-testid="diff-result" style={{ marginTop: 12 }}>
                <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>Build diff vs baseline</div>
                {diff.added.length === 0 && diff.removed.length === 0 && <div className="k-muted" style={{ fontSize: 12 }}>No file-level changes.</div>}
                {diff.added.map(p => <div key={p} className="k-telemetry-positive mono" style={{ fontSize: 12 }}>+ {p}</div>)}
                {diff.removed.map(p => <div key={p} className="k-telemetry-negative mono" style={{ fontSize: 12 }}>- {p}</div>)}
              </div>
            )}
            <EvidenceSatisfaction projectId={projectId} buildId={build.id} />
          </div>

          {/* #38 live preview */}
          {previewHtml != null && (
            <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="live-preview">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Play size={16} color="var(--accent-primary)" /><h3 className="k-heading" style={{ fontSize: 15, margin: 0 }}>Live preview (sandboxed)</h3>
                <button className="k-btn k-btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px", fontSize: 12 }} onClick={() => setPreviewHtml(null)}>Close</button>
              </div>
              <iframe title="preview" data-testid="preview-frame" sandbox="allow-scripts" srcDoc={previewHtml}
                style={{ width: "100%", height: 420, border: "1px solid var(--border)", borderRadius: 10, background: "#fff" }} />
            </div>
          )}

          <div className="k-container" style={{ padding: 18, marginBottom: 14 }} data-testid="file-tree">
            <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0 }}>File tree</h3>
            <div style={{ display: "grid", gridTemplateColumns: openFile ? "1fr 1.4fr" : "1fr", gap: 12 }}>
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                {build.file_list.map(p => (
                  <div key={p} className="k-link mono" data-testid={`file-${p.replace(/\W/g, "-")}`} style={{ fontSize: 12, padding: "3px 0", cursor: "pointer" }} onClick={() => viewFile(p)}>{p}</div>
                ))}
              </div>
              {openFile && (
                <div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                    <span className="k-badge mono" style={{ fontSize: 11 }}>{openFile.path}</span>
                    <button className="k-btn k-btn-ghost" data-testid="explain-file" style={{ padding: "4px 10px", fontSize: 12 }} onClick={explainFile}><Sparkles size={12} /> Explain</button>
                    <button className="k-btn k-btn-ghost" data-testid="regen-module" style={{ padding: "4px 10px", fontSize: 12 }} onClick={regenModule}><RefreshCw size={12} /> Regenerate</button>
                    <button className="k-btn k-btn-ghost" data-testid="edit-file" style={{ padding: "4px 10px", fontSize: 12 }}
                      onClick={() => setEditing({ path: openFile.path, content: openFile.content || "" })}><Pencil size={12} /> Edit</button>
                  </div>
                  {editing ? (
                    <div data-testid="file-editor">
                      <textarea className="k-control mono" style={{ fontSize: 11, height: 320, width: "100%" }}
                        value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} />
                      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                        <button className="k-btn k-btn-primary" data-testid="save-file" style={{ padding: "6px 12px", fontSize: 12 }}
                          onClick={() => { downloadText(editing.path.split("/").pop(), editing.content); toast.success("Edited module downloaded (re-emit)"); }}>
                          <Save size={12} /> Download edited module
                        </button>
                        <button className="k-btn k-btn-ghost" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => setEditing(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <pre className="k-control mono" data-testid="code-viewer" style={{ fontSize: 11, overflow: "auto", maxHeight: 360, whiteSpace: "pre-wrap" }}>{openFile.content}</pre>
                  )}
                  {explain && (
                    <div className="k-callout-accent" data-testid="explain-result" style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--accent-secondary)", marginBottom: 4 }}>Copilot explanation</div>
                      <div className="k-muted" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{explain.loading ? "Thinking…" : explain.text}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {build.next_cards?.length > 0 && (
            <div className="k-container" style={{ padding: 18 }} data-testid="next-cards">
              <h3 className="k-heading" style={{ fontSize: 15, marginTop: 0 }}>What should we build next?</h3>
              <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }} data-testid="next-cards-carousel">
                {build.next_cards.map((c, i) => (
                  <div key={i} className="k-control" data-testid={`next-card-${i}`} style={{ minWidth: 220, flexShrink: 0 }}>
                    <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>{c.title}{c.kind === "compliance" && <span className="k-tag-warn" style={{ fontSize: 11 }}> · compliance</span>}</div>
                    <div className="k-muted" style={{ fontSize: 12, marginTop: 4 }}>{c.prompt}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
